import { Box, BoxProps, Button, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { IDetalharAgrotoxico } from "../../../../../../../types/api/receituarioAgronomico/detalhar-agrotoxico";
import DeleteIcon from "@mui/icons-material/Delete";
import { useCallback, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import DialogAdicionarRelacionamento from "./DialogAdicionarRelacionamento";
import { confirm } from "material-ui-confirm";
import { enqueueSnackbar } from "notistack";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../lib/axios";
import { ICultura } from "../../../../MICulturas/ListarCulturas/Listagem";
import { IPraga } from "../../../../MIPragas/ListarPragas/Listagem";
import { usePermission } from "../../../../../../../components/PermissionGuard";

export default function CardDadosRelacionamentos({
    boxStyle,
    detalhe,
}: CardDadosRelacionamentosProps) {

    const relacionamentos: TRelacionamento[] = detalhe?.relacionamentos ?? [];
    const [openEdit, setOpenEdit] = useState(false);
    const queryClient = useQueryClient();
    const canEdit = usePermission('receituario.editar-agrotoxicos');

    const rows = relacionamentos.map(item => ({
        culturaId: item.cultura?.id,
        culturaNome: item.cultura?.nome,
        pragaId: item.praga?.id,
        pragaNomePopular: item.praga?.nome_popular,
        pragaNomeCientifico: item.praga?.nome_cientifico,
    }));

    const handleRelacionamentosSubmit = () => {
        setOpenEdit(false);
    };

    const { mutate: excluirAgrotoxico } = useMutation({
        mutationKey: "excluir-agrotoxico",
        mutationFn: (data: any) =>
            axiosClient.put(`/receituario-agronomico/agrotoxicos/editar-agrotoxico/${detalhe.id}`, data),
        onError: () => {
            enqueueSnackbar("Erro ao excluir relacionamento!", { variant: "error" });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(`detalhar-agrotoxico/${detalhe.id}`);
            enqueueSnackbar("Relacionamento excluido com sucesso!", {
                variant: "success",
            });
        },
    });

    const handleDeleteSelected = useCallback((praga_id: number, cultura_id: number) => {
        confirm({ description: "Tem certeza que deseja excluir o relacionamento selecionado?" })
            .then(() => excluirAgrotoxico({
                cultura_id: cultura_id,
                praga_id: praga_id,
                agrotoxico_id: detalhe.id,
                action: "excluir"
            }))
            .catch(() => { });
    }, [excluirAgrotoxico, enqueueSnackbar, detalhe.id]);

    return (
        <Box {...boxStyle}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" mb={2}>Relacionamentos</Typography>
                {canEdit && (
                    <Stack direction="row" justifyContent="flex-end">
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            sx={{ fontSize: '0.75rem' }}
                            onClick={() => setOpenEdit(true)}
                        >
                            Adicionar
                        </Button>
                    </Stack>
                )}
            </Stack>
            {rows.length === 0 ? (
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="body2">Nenhuma cultura relacionada.</Typography>
                </Stack>
            ) : (
                <TableContainer sx={{ maxHeight: 500 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Cultura</strong></TableCell>
                                <TableCell><strong>Nome Popular da Praga</strong></TableCell>
                                <TableCell><strong>Nome Científico da Praga</strong></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, idx) => (
                                <TableRow key={`${row.culturaNome}-${row.pragaId}-${idx}`}>
                                    <TableCell>{row.culturaNome}</TableCell>
                                    <TableCell>{row.pragaNomePopular}</TableCell>
                                    <TableCell>
                                        {row.pragaNomeCientifico ? <em>{row.pragaNomeCientifico}</em> : "-"}
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" justifyContent="flex-end" alignItems="center">
                                            <IconButton sx={{ p: 0 }} aria-label="deletar" onClick={() => handleDeleteSelected(row.pragaId, row.culturaId)}>
                                                <DeleteIcon sx={{ mr: 1, color: "gray" }} fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <DialogAdicionarRelacionamento
                open={openEdit}
                value={detalhe.id}
                onClose={() => setOpenEdit(false)}
                onSubmit={handleRelacionamentosSubmit}
            />
        </Box>
    );
}

interface CardDadosRelacionamentosProps {
    boxStyle: BoxProps;
    detalhe: IDetalharAgrotoxico & {
        relacionamentos: TRelacionamento[];
    };
}

export type TRelacionamento = {
    id: number;
    cultura: ICultura;
    praga: IPraga;
}